import { useState, useEffect } from 'react';
import { supabase } from '../../../lib/supabase';

const inputStyle = {
  width: '100%', border: '1.5px solid #DDD', borderRadius: 8,
  padding: '8px 12px', fontSize: '.86rem', outline: 'none', boxSizing: 'border-box',
};

const EMPTY_FORM = {
  name: '', role: 'kasir', pin: '', pin_confirm: '', ref_code: '',
  referral_komisi_pct: '0',
  gaji_pokok: '900000',
  bonus_pos_threshold: '300',
  bonus_pos_per_pcs: '100',
};

function makeReferralLink(ref_code) {
  return `${window.location.origin}/?ref=${encodeURIComponent(ref_code)}`;
}

function Label({ children }) {
  return <label style={{ display: 'block', fontSize: '.74rem', color: 'var(--muted)', marginBottom: 4 }}>{children}</label>;
}

function SectionDivider({ children }) {
  return (
    <div style={{ gridColumn: '1/-1', borderTop: '1px solid var(--border)', paddingTop: 14, marginTop: 4 }}>
      <div style={{ fontFamily: "'DM Mono',monospace", fontSize: '.58rem', letterSpacing: '1.6px', textTransform: 'uppercase', color: 'rgba(201,146,42,.7)', marginBottom: 12 }}>
        {children}
      </div>
    </div>
  );
}

export default function Pengguna() {
  const [users, setUsers]       = useState([]);
  const [form, setForm]         = useState(EMPTY_FORM);
  const [editId, setEditId]     = useState(null);
  const [showForm, setShowForm] = useState(false);
  const [err, setErr]           = useState('');
  const [copied, setCopied]     = useState(null);

  useEffect(() => { load(); }, []);

  async function load() {
    const { data } = await supabase
      .from('users')
      .select('id,name,role,is_active,ref_code,referral_komisi_pct,gaji_pokok,bonus_pos_threshold,bonus_pos_per_pcs,created_at')
      .order('role').order('name');
    setUsers(data || []);
  }

  async function handleSave() {
    setErr('');
    const trimmedName    = (form.name     || '').trim();
    const trimmedRefCode = (form.ref_code || '').trim().toLowerCase();
    const komisiPct      = parseFloat(form.referral_komisi_pct) || 0;
    const gajiPokok      = parseInt(form.gaji_pokok,          10) || 0;
    const threshold      = parseInt(form.bonus_pos_threshold,  10) || 0;
    const perPcs         = parseFloat(form.bonus_pos_per_pcs)     || 0;

    if (!trimmedName)                              { setErr('Nama wajib diisi'); return; }
    if (!editId && !form.pin)                      { setErr('PIN wajib diisi untuk pengguna baru'); return; }
    if (form.pin && form.pin !== form.pin_confirm) { setErr('PIN tidak cocok'); return; }
    if (form.pin && !/^\d{4,6}$/.test(form.pin))  { setErr('PIN harus 4-6 angka'); return; }
    if (komisiPct < 0 || komisiPct > 100)          { setErr('Komisi harus antara 0–100%'); return; }
    if (gajiPokok < 0)                             { setErr('Gaji pokok tidak boleh negatif'); return; }
    if (threshold < 0)                             { setErr('Threshold tidak boleh negatif'); return; }
    if (perPcs < 0)                                { setErr('Bonus per pcs tidak boleh negatif'); return; }

    if (trimmedRefCode) {
      const { data: existing } = await supabase
        .from('users').select('id')
        .eq('ref_code', trimmedRefCode)
        .neq('id', editId || '00000000-0000-0000-0000-000000000000')
        .maybeSingle();
      if (existing) { setErr('Kode referral sudah dipakai pengguna lain'); return; }
    }

    const payload = {
      name:                trimmedName,
      role:                form.role,
      ref_code:            trimmedRefCode || null,
      referral_komisi_pct: komisiPct,
      gaji_pokok:          gajiPokok,
      bonus_pos_threshold: threshold,
      bonus_pos_per_pcs:   perPcs,
    };

    if (form.pin) {
      const enc = new TextEncoder();
      const buf = await crypto.subtle.digest('SHA-256', enc.encode(form.pin));
      payload.pin_hash = Array.from(new Uint8Array(buf))
        .map(b => b.toString(16).padStart(2, '0')).join('');
    }

    let dbError;
    if (editId) {
      const { error } = await supabase.from('users').update(payload).eq('id', editId);
      dbError = error;
    } else {
      const { error } = await supabase.from('users').insert({ ...payload, is_active: true });
      dbError = error;
    }

    if (dbError) {
      setErr('Gagal menyimpan: ' + dbError.message);
      return;
    }

    reset(); load();
  }

  async function toggleActive(user) {
    if (user.role === 'admin' && user.is_active) {
      const admins = users.filter(u => u.role === 'admin' && u.is_active);
      if (admins.length <= 1) { alert('Tidak bisa menonaktifkan admin terakhir.'); return; }
    }
    await supabase.from('users').update({ is_active: !user.is_active }).eq('id', user.id);
    load();
  }

  function handleEdit(u) {
    setForm({
      name:                u.name,
      role:                u.role,
      pin:                 '',
      pin_confirm:         '',
      ref_code:            u.ref_code || '',
      referral_komisi_pct: String(u.referral_komisi_pct ?? 0),
      gaji_pokok:          String(u.gaji_pokok          ?? 900000),
      bonus_pos_threshold: String(u.bonus_pos_threshold  ?? 300),
      bonus_pos_per_pcs:   String(u.bonus_pos_per_pcs    ?? 100),
    });
    setEditId(u.id); setShowForm(true); window.scrollTo(0, 0);
  }

  function reset() { setForm(EMPTY_FORM); setEditId(null); setShowForm(false); setErr(''); }

  function autoRef() {
    const base = (form.name || '').trim().toLowerCase()
      .replace(/\s+/g, '').replace(/[^a-z0-9]/g, '');
    setForm(f => ({ ...f, ref_code: base || 'ref' + Math.floor(Math.random() * 9000 + 1000) }));
  }

  async function copyLink(user) {
    const link = makeReferralLink(user.ref_code);
    try {
      await navigator.clipboard.writeText(link);
      setCopied(user.id);
      setTimeout(() => setCopied(null), 2000);
    } catch {
      prompt('Salin link referral ini:', link);
    }
  }

  async function shareLink(user) {
    const link = makeReferralLink(user.ref_code);
    const text  = `Halo! Order Cireng Ayam Suwir Pedas HAPin lewat link ini ya 👇\n${link}`;
    if (navigator.share) {
      try { await navigator.share({ title: 'HAPin - Referral', text, url: link }); return; }
      catch { /* fallback */ }
    }
    window.open(`https://wa.me/?text=${encodeURIComponent(text)}`, '_blank');
  }

  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));
  const fmtRp = n => 'Rp ' + (Number(n) || 0).toLocaleString('id-ID');
  const active   = users.filter(u =>  u.is_active);
  const inactive = users.filter(u => !u.is_active);

  const UserRow = ({ u }) => (
    <div style={{ padding: '13px 18px', borderBottom: '1px solid var(--border)', opacity: u.is_active ? 1 : 0.5 }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 10 }}>
        <div style={{ minWidth: 0 }}>
          <div style={{ fontWeight: 500, fontSize: '.88rem' }}>{u.name}</div>
          <div style={{ fontSize: '.74rem', color: 'var(--muted)', marginTop: 3, display: 'flex', alignItems: 'center', gap: 6, flexWrap: 'wrap' }}>
            <span style={{ fontFamily: "'DM Mono',monospace", fontSize: '.63rem', background: u.role === 'admin' ? 'rgba(201,146,42,.12)' : '#F5F5F5', color: u.role === 'admin' ? 'var(--gold)' : 'var(--muted)', borderRadius: 20, padding: '1px 7px' }}>
              {u.role}
            </span>
            {/* Gaji pokok */}
            <span style={{ fontFamily: "'DM Mono',monospace", fontSize: '.62rem', background: '#F5F0E8', color: 'var(--brown)', borderRadius: 20, padding: '1px 7px' }}>
              {fmtRp(u.gaji_pokok ?? 900000)}/bln
            </span>
            {/* Threshold & bonus per pcs */}
            <span style={{ fontFamily: "'DM Mono',monospace", fontSize: '.62rem', color: '#6B7280' }}>
              &gt;{u.bonus_pos_threshold ?? 300} pcs → +{fmtRp(u.bonus_pos_per_pcs ?? 100)}/pcs
            </span>
            {u.ref_code && (
              <span style={{ fontFamily: "'DM Mono',monospace", fontSize: '.62rem', color: '#6B7280' }}>
                ref: {u.ref_code}
              </span>
            )}
            {u.referral_komisi_pct > 0 && (
              <span style={{ fontFamily: "'DM Mono',monospace", fontSize: '.62rem', background: '#FFF8E7', color: 'var(--brown)', borderRadius: 20, padding: '1px 7px' }}>
                komisi PO {u.referral_komisi_pct}%
              </span>
            )}
            {!u.is_active && <span style={{ color: '#C62828' }}>nonaktif</span>}
          </div>
        </div>

        <div style={{ display: 'flex', gap: 6, flexShrink: 0, flexWrap: 'wrap', justifyContent: 'flex-end' }}>
          {u.ref_code && u.is_active && (
            <>
              <button onClick={() => shareLink(u)} style={{ padding: '4px 10px', borderRadius: 6, background: '#F0FBF4', border: '1px solid #A5D6A7', color: '#1B5E20', fontSize: '.72rem', cursor: 'pointer', whiteSpace: 'nowrap' }}>
                📤 Share
              </button>
              <button onClick={() => copyLink(u)} style={{ padding: '4px 10px', borderRadius: 6, background: copied === u.id ? '#E8F5E9' : '#EFF6FF', border: `1px solid ${copied === u.id ? '#A5D6A7' : '#BFDBFE'}`, color: copied === u.id ? '#1B5E20' : '#1C5BD4', fontSize: '.72rem', cursor: 'pointer', whiteSpace: 'nowrap', transition: 'all .2s' }}>
                {copied === u.id ? '✓ Tersalin' : '🔗 Salin Link'}
              </button>
            </>
          )}
          <button onClick={() => handleEdit(u)} style={{ padding: '4px 10px', borderRadius: 6, background: 'var(--cream)', border: '1px solid var(--border)', fontSize: '.72rem', cursor: 'pointer' }}>Edit</button>
          <button onClick={() => toggleActive(u)} style={{ padding: '4px 10px', borderRadius: 6, background: u.is_active ? '#FFF3E0' : '#E8F5E9', border: `1px solid ${u.is_active ? '#FFCC80' : '#A5D6A7'}`, color: u.is_active ? '#E65100' : '#1B5E20', fontSize: '.72rem', cursor: 'pointer' }}>
            {u.is_active ? 'Nonaktifkan' : 'Aktifkan'}
          </button>
        </div>
      </div>

      {u.ref_code && u.is_active && (
        <div style={{ marginTop: 6, fontSize: '.68rem', color: '#9CA3AF', fontFamily: "'DM Mono',monospace", background: '#F9FAFB', padding: '4px 10px', borderRadius: 6, wordBreak: 'break-all', display: 'inline-block', maxWidth: '100%' }}>
          {makeReferralLink(u.ref_code)}
        </div>
      )}
    </div>
  );

  return (
    <div style={{ padding: 'clamp(16px,4vw,28px) clamp(12px,4vw,24px) 60px', maxWidth: 640 }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 20 }}>
        <div>
          <div style={{ fontFamily: "'Baloo 2',sans-serif", fontWeight: 700, fontSize: '1.5rem' }}>Pengguna POS</div>
          <div style={{ fontSize: '.82rem', color: 'var(--muted)' }}>{active.length} aktif · {inactive.length} nonaktif</div>
        </div>
        <button onClick={() => { reset(); setShowForm(true); }} style={{ padding: '9px 18px', borderRadius: 8, background: '#CD5226', color: '#fff', border: 'none', fontSize: '.84rem', cursor: 'pointer' }}>
          + Tambah
        </button>
      </div>

      {showForm && (
        <div className="fade-up" style={{ background: 'white', borderRadius: 'var(--radius)', border: '1px solid var(--border)', padding: '22px', marginBottom: 20, boxShadow: 'var(--shadow)' }}>
          <div style={{ fontFamily: "'Baloo 2',sans-serif", fontWeight: 700, fontSize: '1.1rem', marginBottom: 18 }}>
            {editId ? 'Edit Pengguna' : 'Pengguna Baru'}
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 14, marginBottom: 14 }}>
            {/* Nama */}
            <div style={{ gridColumn: '1/-1' }}>
              <Label>Nama *</Label>
              <input value={form.name} onChange={e => set('name', e.target.value)} placeholder="Nama kasir / reseller" style={inputStyle} />
            </div>

            {/* Role */}
            <div>
              <Label>Role</Label>
              <select value={form.role} onChange={e => set('role', e.target.value)} style={inputStyle}>
                <option value="kasir">Kasir</option>
                <option value="admin">Admin</option>
              </select>
            </div>

            {/* Komisi PO */}
            <div>
              <Label>Komisi Referral PO (%)</Label>
              <input
                type="number" min="0" max="100" step="0.5"
                value={form.referral_komisi_pct}
                onChange={e => set('referral_komisi_pct', e.target.value)}
                placeholder="0"
                style={inputStyle}
              />
            </div>

            {/* PIN */}
            <div>
              <Label>{editId ? 'PIN Baru (kosongkan jika tidak ganti)' : 'PIN *'}</Label>
              <input type="password" inputMode="numeric" maxLength={6} value={form.pin}
                onChange={e => set('pin', e.target.value)} placeholder="4-6 angka" style={inputStyle} />
            </div>

            {/* Konfirmasi PIN */}
            <div>
              <Label>Konfirmasi PIN</Label>
              <input type="password" inputMode="numeric" maxLength={6} value={form.pin_confirm}
                onChange={e => set('pin_confirm', e.target.value)} placeholder="Ulangi PIN" style={inputStyle} />
            </div>

            {/* Kode Referral */}
            <div style={{ gridColumn: '1/-1' }}>
              <Label>
                Kode Referral{' '}
                <span style={{ fontWeight: 400, color: '#9CA3AF' }}>(opsional — untuk tracking order dari kasir/reseller ini)</span>
              </Label>
              <div style={{ display: 'flex', gap: 8 }}>
                <input
                  value={form.ref_code}
                  onChange={e => set('ref_code', e.target.value.toLowerCase().replace(/[^a-z0-9]/g, ''))}
                  placeholder="contoh: yoyo"
                  style={{ ...inputStyle, flex: 1 }}
                />
                <button type="button" onClick={autoRef} style={{ padding: '8px 12px', borderRadius: 8, background: '#F3F4F6', border: '1px solid var(--border)', fontSize: '.75rem', cursor: 'pointer', whiteSpace: 'nowrap', color: 'var(--muted)' }}>
                  Auto
                </button>
              </div>
              {form.ref_code && (
                <div style={{ marginTop: 6, fontSize: '.71rem', color: '#6B7280', fontFamily: "'DM Mono',monospace", background: '#F9FAFB', padding: '5px 10px', borderRadius: 6, wordBreak: 'break-all' }}>
                  {makeReferralLink(form.ref_code)}
                </div>
              )}
            </div>

            {/* ── Konfigurasi Gaji ─────────────────────────────── */}
            <SectionDivider>Konfigurasi Gaji & Bonus POS</SectionDivider>

            {/* Gaji Pokok */}
            <div style={{ gridColumn: '1/-1' }}>
              <Label>Gaji Pokok (Rp/bulan)</Label>
              <input
                type="number" min="0" step="50000"
                value={form.gaji_pokok}
                onChange={e => set('gaji_pokok', e.target.value)}
                placeholder="900000"
                style={inputStyle}
              />
            </div>

            {/* Threshold */}
            <div>
              <Label>Threshold POS (pcs/hari)</Label>
              <input
                type="number" min="0" step="10"
                value={form.bonus_pos_threshold}
                onChange={e => set('bonus_pos_threshold', e.target.value)}
                placeholder="300"
                style={inputStyle}
              />
            </div>

            {/* Bonus per pcs */}
            <div>
              <Label>Bonus per pcs di atas threshold (Rp)</Label>
              <input
                type="number" min="0" step="10"
                value={form.bonus_pos_per_pcs}
                onChange={e => set('bonus_pos_per_pcs', e.target.value)}
                placeholder="100"
                style={inputStyle}
              />
            </div>

            {/* Preview kalkulasi */}
            {(form.bonus_pos_threshold || form.bonus_pos_per_pcs) && (
              <div style={{ gridColumn: '1/-1', background: '#FDFAF6', borderRadius: 8, padding: '10px 14px', fontSize: '.76rem', color: 'var(--muted)', border: '1px dashed var(--border)' }}>
                Contoh: jika terjual <strong>{(parseInt(form.bonus_pos_threshold) || 300) + 50} pcs</strong> dalam sehari →
                bonus = 50 pcs × {fmtRp(form.bonus_pos_per_pcs || 100)} = <strong style={{ color: '#856404' }}>{fmtRp(50 * (parseFloat(form.bonus_pos_per_pcs) || 100))}</strong>
              </div>
            )}
          </div>

          {err && (
            <div style={{ background: '#FFEBEE', borderRadius: 6, padding: '7px 12px', fontSize: '.78rem', color: '#C62828', marginBottom: 12 }}>{err}</div>
          )}

          <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end' }}>
            <button onClick={reset} style={{ padding: '8px 16px', borderRadius: 8, background: 'var(--cream)', border: '1px solid var(--border)', fontSize: '.82rem', cursor: 'pointer', color: 'var(--muted)' }}>Batal</button>
            <button onClick={handleSave} style={{ padding: '8px 18px', borderRadius: 8, background: '#CD5226', color: '#fff', border: 'none', fontSize: '.82rem', cursor: 'pointer' }}>
              {editId ? 'Simpan' : 'Tambah Pengguna'}
            </button>
          </div>
        </div>
      )}

      <div style={{ background: 'white', borderRadius: 'var(--radius)', border: '1px solid var(--border)', overflow: 'hidden', boxShadow: 'var(--shadow)' }}>
        {users.length === 0
          ? <div style={{ padding: 32, textAlign: 'center', color: 'var(--muted)', fontSize: '.84rem' }}>Belum ada pengguna</div>
          : users.map(u => <UserRow key={u.id} u={u} />)
        }
      </div>
    </div>
  );
}
